import React, { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarDaysIcon,
  ClockIcon,
  KeyIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import UserPermissionsModal from '../components/UserPermissionsModal';
import RolePermissionsSelector from '../components/RolePermissionsSelector';

const UserManagement = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role_ids: [],
    selected_permissions: [],
    api_access_enabled: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse, permissionsResponse] = await Promise.all([
        api.getOrganizationUsers(),
        api.getRolesWithPermissions(),
        api.getPermissions()
      ]);

      setUsers(usersResponse.data.users || []);
      setRoles(rolesResponse.data.roles || []);
      setPermissions(permissionsResponse.data.permissions || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to manage users');
      } else {
        toast.error('Failed to load user management data');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreateModal = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role_ids: [],
      selected_permissions: [],
      api_access_enabled: true
    });
    setIsEditing(false);
    setShowUserModal(true);
  };

  const openPermissionsModal = (user) => {
    setPermissionsUser(user);
    setShowPermissionsModal(true);
  };

  const openEditModal = async (user) => {
    try {
      // Fetch user's current permissions
      const permissionsResponse = await api.getUserPermissions(user.id);
      const userPermissions = permissionsResponse.data.permissions || {};
      
      // Extract all current permission names
      const currentPermissionNames = [];
      if (userPermissions.role_based) {
        currentPermissionNames.push(...userPermissions.role_based.map(p => p.permission_name));
      }
      if (userPermissions.direct_grants) {
        currentPermissionNames.push(...userPermissions.direct_grants.map(p => p.permission_name));
      }

      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role_ids: user.roles ? user.roles.map(role => role.id) : [],
        selected_permissions: [...new Set(currentPermissionNames)], // Remove duplicates
        api_access_enabled: user.api_access_enabled !== false
      });
    } catch (error) {
      console.error('Error loading user permissions:', error);
      // Fallback to basic form data if permissions loading fails
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role_ids: user.roles ? user.roles.map(role => role.id) : [],
        selected_permissions: [],
        api_access_enabled: user.api_access_enabled !== false
      });
      toast.error('Failed to load current permissions, but you can still edit the user');
    }
    
    setSelectedUser(user);
    setIsEditing(true);
    setShowUserModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitting) return; // Prevent double submission
    
    setSubmitting(true);
    try {
      let userId;
      
      // Step 1: Create/Update the user with basic info
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        api_access_enabled: formData.api_access_enabled
      };
      
      if (isEditing) {
        await api.updateOrganizationUser(selectedUser.id, userData);
        userId = selectedUser.id;
      } else {
        const response = await api.createOrganizationUser(userData);
        userId = response.data.user.id;
      }
      
      // Step 2: Handle role assignments using new flattened system
      if (isEditing) {
        // When editing, we need to handle role changes properly
        const currentRoleNames = selectedUser.roles ? selectedUser.roles.map(role => role.name) : [];
        const newRoleNames = roles
          .filter(role => formData.role_ids.includes(role.id))
          .map(role => role.name);

        // Remove roles that are no longer selected
        const rolesToRemove = currentRoleNames.filter(roleName => !newRoleNames.includes(roleName));
        for (const roleName of rolesToRemove) {
          try {
            await api.removeUserRole(userId, roleName);
          } catch (roleError) {
            console.warn(`Failed to remove role ${roleName}:`, roleError);
          }
        }

        // Add roles that are newly selected
        const rolesToAdd = newRoleNames.filter(roleName => !currentRoleNames.includes(roleName));
        for (const roleName of rolesToAdd) {
          try {
            await api.assignUserRole(userId, { role_name: roleName });
          } catch (roleError) {
            console.warn(`Failed to assign role ${roleName}:`, roleError);
          }
        }
      } else {
        // When creating, just assign all selected roles
        const selectedRoleNames = roles
          .filter(role => formData.role_ids.includes(role.id))
          .map(role => role.name);
        
        for (const roleName of selectedRoleNames) {
          try {
            await api.assignUserRole(userId, { role_name: roleName });
          } catch (roleError) {
            console.warn(`Failed to assign role ${roleName}:`, roleError);
          }
        }
      }
      
      // Step 3: Handle custom permission modifications
      // After role changes, sync the actual permissions with what's selected
      if (isEditing && formData.selected_permissions.length > 0) {
        // Get current permissions after role changes
        const updatedPermissionsResponse = await api.getUserPermissions(userId);
        const currentPermissions = [];
        const updatedPerms = updatedPermissionsResponse.data.permissions || {};
        
        if (updatedPerms.role_based) {
          currentPermissions.push(...updatedPerms.role_based.map(p => p.permission_name));
        }
        if (updatedPerms.direct_grants) {
          currentPermissions.push(...updatedPerms.direct_grants.map(p => p.permission_name));
        }
        
        const currentPermSet = new Set(currentPermissions);
        const selectedPermSet = new Set(formData.selected_permissions);
        
        // Find permissions to add and remove
        const permissionsToAdd = formData.selected_permissions.filter(perm => !currentPermSet.has(perm));
        const permissionsToRemove = currentPermissions.filter(perm => !selectedPermSet.has(perm));
        
        // Bulk update permissions if there are changes
        if (permissionsToAdd.length > 0 || permissionsToRemove.length > 0) {
          try {
            await api.bulkUpdateUserPermissions(userId, {
              add: permissionsToAdd,
              remove: permissionsToRemove
            });
          } catch (permError) {
            console.warn('Failed to update permissions:', permError);
            toast.error('User updated but some permission changes failed');
          }
        }
      } else if (!isEditing && formData.selected_permissions.length > 0) {
        // For new users, handle any additional permissions beyond roles
        const rolePermissions = new Set();
        roles.forEach(role => {
          if (formData.role_ids.includes(role.id) && role.permissions) {
            Object.values(role.permissions).flat().forEach(perm => {
              rolePermissions.add(perm.name);
            });
          }
        });
        
        const additionalPermissions = formData.selected_permissions.filter(
          perm => !rolePermissions.has(perm)
        );
        
        if (additionalPermissions.length > 0) {
          try {
            await api.bulkUpdateUserPermissions(userId, {
              add: additionalPermissions,
              remove: []
            });
          } catch (permError) {
            console.warn('Failed to add additional permissions:', permError);
          }
        }
      }
      
      toast.success(isEditing ? 'User updated successfully! 🎉' : 'User created successfully! Welcome to the team! 🎉');
      setShowUserModal(false);
      await fetchData(); // Refresh the user list
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(isEditing ? 'Failed to update user. Please try again.' : 'Failed to create user. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return; // Prevent double deletion
    
    setDeleting(true);
    try {
      await api.deleteOrganizationUser(userToDelete.id);
      toast.success(`${userToDelete.first_name} ${userToDelete.last_name} has been removed from the team`);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      await fetchData(); // Refresh the user list
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to delete user. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleRoleToggle = (roleId) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleDisplay = (userRoles) => {
    if (!userRoles || userRoles.length === 0) return 'No roles assigned';
    return userRoles.map(role => role.display_name).join(', ');
  };

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'admin': 'bg-red-100 text-red-800',
      'manager': 'bg-blue-100 text-blue-800',
      'editor': 'bg-green-100 text-green-800',
      'viewer': 'bg-gray-100 text-gray-800',
      'agent_creator': 'bg-purple-100 text-purple-800'
    };
    return colors[roleName] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-whatsapp-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-whatsapp-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600">Manage users, roles, and permissions for your organization</p>
            </div>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center space-x-2 bg-whatsapp-600 text-white px-4 py-2 rounded-lg hover:bg-whatsapp-700 transition-colors"
          >
            <UserPlusIcon className="h-5 w-5" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-whatsapp-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Organization Users ({filteredUsers.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  API Access
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-whatsapp-100 rounded-full flex items-center justify-center">
                        <span className="text-whatsapp-600 font-medium">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-1" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {user.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role) => (
                          <span
                            key={role.id}
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(role.name)}`}
                          >
                            {role.display_name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">No roles assigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.api_access_enabled ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckIcon className="h-3 w-3 mr-1" />
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XMarkIcon className="h-3 w-3 mr-1" />
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {formatDate(user.last_login_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-4 w-4 mr-1" />
                      {formatDate(user.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openPermissionsModal(user)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                        title="Manage permissions"
                      >
                        <KeyIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="text-whatsapp-600 hover:text-whatsapp-900 transition-colors"
                        title="Edit user"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => confirmDelete(user)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete user"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="p-6 text-center text-gray-500">
            <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No users found</p>
          </div>
        )}
      </div>

      {/* User Permissions Modal */}
      <UserPermissionsModal
        user={permissionsUser}
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        onUpdate={fetchData}
      />

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 pb-8 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? 'Edit User' : 'Create New User'}
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-whatsapp-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-whatsapp-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  disabled={isEditing}
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-whatsapp-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-whatsapp-500 focus:border-transparent"
                />
              </div>

              <div>
                <RolePermissionsSelector
                  selectedRoles={formData.role_ids}
                  onRolesChange={(roleIds) => setFormData(prev => ({ ...prev, role_ids: roleIds }))}
                  selectedPermissions={formData.selected_permissions}
                  onPermissionsChange={(permissions) => setFormData(prev => ({ ...prev, selected_permissions: permissions }))}
                  availableRoles={roles}
                  availablePermissions={permissions}
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.api_access_enabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, api_access_enabled: e.target.checked }))}
                    className="rounded border-gray-300 text-whatsapp-600 focus:ring-whatsapp-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Enable API Access</div>
                    <div className="text-xs text-gray-500">Allow this user to use API endpoints</div>
                  </div>
                </label>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-6 mt-4">
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-whatsapp-600 text-white rounded-md hover:bg-whatsapp-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[120px] justify-center transition-all duration-200"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      <span>{isEditing ? 'Update User' : 'Create User'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Delete User</h3>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to delete <strong>{userToDelete.first_name} {userToDelete.last_name}</strong>?
                This action cannot be undone.
              </p>
              <div className="flex items-center justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 min-w-[120px] justify-center transition-all duration-200"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      <span>Delete User</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;